import { useRef, useState } from 'react';

import { useAutosizeTextArea } from '~/hooks/useAutosizeTextArea';

import { PaperPlaneIcon } from '@radix-ui/react-icons';
import { StopIcon } from '~/components/icons/StopIcon';

import { Button } from '~/components/ui/Button';
import { Textarea, defaultTextareaStyles } from '~/components/ui/textarea';
import UploadImagePreview from '~/components/playground/upload-image-preview';
import FileUploader from './chat/file-uploader';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

export function LLMInput(props: {
  disabled?: boolean;
  onSubmit: (text: string, base64Files?: string[]) => void;

  multiModal?: boolean;
  isLoading?: boolean;
  onAbordRequest?: () => void;
  onFileUpload?: (file: File) => void;
  onFileError?: (error: string) => void;
}) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [value, setValue] = useState('');
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useAutosizeTextArea(textAreaRef.current, value);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = event.target?.value;

    setValue(val);
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const message = e.currentTarget.elements.namedItem('message') as HTMLInputElement;

    if (imageUrl) {
      props.onSubmit(message.value, [imageUrl]);
      setImageUrl(null);
      setValue('');
      return;
    }
    props.onSubmit(message.value);
    setTimeout(() => {
      setValue('');
    }, 10);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // ignore if the user is using IME
    if (event.nativeEvent.isComposing || event.keyCode === 229) {
      return;
    }

    // On initial enter click if the input value is empty return early
    if (event.key === 'Enter' && value.length < 1) {
      return;
    }

    // Prevents adding a new line
    if (
      ((event.which || event.keyCode) === 13 || event.key === 'Enter') &&
      !event.shiftKey &&
      !event.altKey &&
      !event.ctrlKey &&
      !event.metaKey
    ) {
      // Before we submit we want to prevent the form's default behavior of resetting and jumping for a smooth experience
      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
    } else if (event.key === 'Enter') {
      // Adds a new line
      setValue(value);
    } else {
      setValue(event.currentTarget.value);
    }
  };

  const onRemovePreviewImage = () => setImageUrl(null);

  const handleUploadImageFile = async (file: File) => {
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.addEventListener('load', () => resolve(reader.result as string));
      reader.addEventListener('error', (error) => reject(error));
    });
    setImageUrl(base64);
  };

  const handleUploadFile = async (file: File) => {
    try {
      if (props.multiModal && file.type.startsWith('image/')) {
        return await handleUploadImageFile(file);
      }
      props.onFileUpload?.(file);
    } catch (error: any) {
      props.onFileError?.(error.message);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className={`mx-8 my-2 space-y-4 ${defaultTextareaStyles} [&:has(textarea:focus)]:ring-input-active [&:has(textarea:focus)]:border-input-active `}
    >
      {imageUrl && <UploadImagePreview url={imageUrl} onRemove={onRemovePreviewImage} />}
      <div className="flex items-end gap-2">
        <Textarea
          autoFocus
          variant="transparent"
          disabled={props.disabled}
          name="message"
          onKeyDown={handleKeyDown}
          placeholder="Type a message"
          ref={textAreaRef}
          rows={1}
          className="min-h-[38px] w-full grow resize-none self-center overflow-y-auto ring-0 focus:ring-0 focus-visible:ring-0"
          value={value}
          onChange={handleChange}
        />
        {props.multiModal && (
          <FileUploader
            onFileUpload={handleUploadFile}
            onFileError={props.onFileError}
            config={{
              disabled: props.disabled || false,
            }}
          />
        )}
        {props.isLoading ? (
          <Button variant="ghost" onClick={props.onAbordRequest}>
            {/* add loading button here*/}
            <StopIcon role="graphics-symbol" aria-label="Stop last sent prompt" className="h-6 w-6" />
          </Button>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Button variant="ghost" disabled={props.disabled || !value.trim()} type="submit">
                  <PaperPlaneIcon role="graphics-symbol" aria-labelledby="button-send-title" className="h-6 w-6" />
                </Button>
              </div>
            </TooltipTrigger>
            <TooltipContent id="button-send-title">
              {value.trim() ? <p>Send</p> : <p>Enter a prompt to send</p>}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </form>
  );
}
